import React from "react";
import { Box } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import CustomLabel from "../../components/CustomLabel";
import PageHeader from "../../components/PageHeader";
import { useParams } from "react-router-dom";
import { getAllBranches } from "../../services/BranchesService";
import { getRefunds } from "../../services/newReportsService";
import { formatTableDate } from "../../utils/tableDateFormat";
const RefundsPage: React.FC = () => {
  const params = useParams<{ businessId?: string; id?: string }>();
  const businessId = params.businessId || params.id;

  if (!businessId) {
    throw new Error("Revenue report requires businessId in route");
  }

  
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [units, setUnits] = React.useState<any[]>([]);
  const [selectValue, setSelectValue] = React.useState<string | number>("all");
const [refunds, setRefunds] = React.useState<any[]>([]);
const [refundsLoading, setRefundsLoading] = React.useState(false);

const [refundFilters, setRefundFilters] = React.useState<{
  from?: string;
  to?: string;
  businessId?: string;
  unitId?: string;
}>({
  from: undefined,
  to: undefined,
  businessId: undefined,
  unitId: undefined,
});
  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | null
  >(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
    return { start: start.toISOString(), end: end.toISOString() };
  });

  const buildDateBoundary = (raw: string | Date | null | undefined, endOfDay: boolean) => {
    if (!raw) return null;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0));
    }
    const parsed = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(
      Date.UTC(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate(),
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 999 : 0,
      ),
    );
  };

  const normalizeRangeIso = (range: { start?: string; end?: string } | null) => {
    if (!range) return { from: undefined as string | undefined, to: undefined as string | undefined };
    const fromDate = buildDateBoundary(range.start, false);
    const toDate = buildDateBoundary(range.end, true);
    return {
      from: fromDate ? fromDate.toISOString() : undefined,
      to: toDate ? toDate.toISOString() : undefined,
    };
  };

  const getUnitId = (u: any) => u.id ?? u.unit_id;

  React.useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await getAllBranches(businessId);
        const unitsData = res?.units || res?.data || res || [];
        setUnits(Array.isArray(unitsData) ? unitsData : []);
      } catch {
        setUnits([]);
      }
    };

    fetchUnits();
  }, [businessId]);

const fetchRefunds = async () => {
  setRefundsLoading(true);
  try {
    const data = await getRefunds(refundFilters) as any;
    const refundsWithUnits = Array.isArray(data) ? data.map((refund: any) => {
      const unit = units.find(u => getUnitId(u) === refund.unitId);
      return {
        ...refund,
        unitName: unit?.name || "Unknown Unit"
      };
    }) : [];
    setRefunds(refundsWithUnits);
  } catch (err) {
    console.error("Failed to fetch refunds", err);
    setRefunds([]);
  } finally {
    setRefundsLoading(false);
  }
};

React.useEffect(() => {
  const { from, to } = normalizeRangeIso(dateRange);
  setRefundFilters({
    businessId,
    from,
    to,
    unitId: selectValue === "all" ? undefined : String(selectValue),
  });
}, [businessId, dateRange, selectValue]);

React.useEffect(() => {
  if (units.length > 0) {
    fetchRefunds();
  }
}, [refundFilters, units]);


  const exportCsv = React.useCallback(() => {
    if (!refunds.length) return;

    const keys = ["clientName", "invoiceNumber", "amount", "unitName", "date"];
    const header = keys.join(",");
    const lines = refunds.map((r) => {
      const row: Record<string, any> = {
        clientName: r.clientName ?? "-",
        invoiceNumber: r.invoiceNumber ?? "-",
        amount: r.amount !== undefined && r.amount !== null
          ? `$${Number(r.amount).toFixed(2)}`
          : "-",
        unitName: r.unitName ?? "-",
        date: (() => {
          const d = new Date(r.date);
          return Number.isNaN(d.getTime()) ? "-" : formatTableDate(d);
        })(),
      };
      return keys.map((k) => `"${String(row[k] ?? "-").replace(/"/g, '""')}"`).join(",");
    });

    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `refunds-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [refunds]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <PageHeader
        title="Refunds"
        subtitle="Refunds issued in the selected period."
        sx={{ mt: { xs: 2, sm: 3 } }}
      />

      <SearchHeaderContainer
        search={search}
        setSearch={setSearch}
        dateRangeConfig={{
          value: dateRange ?? undefined,
          onChange: (v: any) => setDateRange(v),
        }}
        selectConfig={{
          options: [
            { label: "All", value: "all" },
            ...units.map((u) => ({
              label: u.name || "Unknown Branch",
              value: getUnitId(u),
            })),
          ],
          value: selectValue,
          onChange: (v: any) => setSelectValue(v),
          label: "Select Branch",
        }}
        buttonConfig={[
          {
            label: "Export CSV",
            onClick: () => Promise.resolve(exportCsv()),
            startIcon: <FileDownloadIcon />,
            bgColor: COLORS.MainColor,
            textColor: "#fff",
          },
        ]}
      />

      <CustomTable<any>
        filters={{ page: 0, size: 10, search }}
        clientSidePagination
        loading={loading}
        fields={[
          { key: "clientName", header: "Client Name" },
          { key: "invoiceNumber", header: "Invoice #" },
          { key: "amount", header: "Amount Refunded", render: (row: any) => `$${Number(row.amount).toFixed(2)}` },
          { key: "unitName", header: "Unit" },
          { key: "date", header: "Date", render: (row: any) => formatTableDate(row.date) },
        ]}
        fetchFunc={async (params?: any) => {
          const q = String(params?.search || search || "")
            .toLowerCase()
            .trim();

          const filtered = refunds.filter((r) => {
            if (q) {
              const text = `${r.clientName} ${r.invoiceNumber} ${r.amount} ${r.unitName} ${r.date}`.toLowerCase();
              if (!text.includes(q)) return false;
            }

            if (dateRange) {
              const d = new Date(r.date);
              const fromDate = buildDateBoundary(dateRange.start, false);
              const toDate = buildDateBoundary(dateRange.end, true);
              if (fromDate && d < fromDate) return false;
              if (toDate && d > toDate) return false;
            }

            return true;
          });

          return {
            content: filtered,
            number: 0,
            size: params?.size || 10,
            totalElements: filtered.length,
            totalPages: Math.max(1, Math.ceil(filtered.length / (params?.size || 10))),
          };
        }}
      />

      <Box
        sx={{
          backgroundColor: COLORS.WhiteBackground,
          borderRadius: "0 0 10px 10px",
          padding: "16px",
          marginTop: "-10px",
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <CustomLabel text="Total Refunded:" />
        <CustomLabel
          text={new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(refunds.reduce((sum, r) => sum + Number(r.amount || 0), 0))}
          fontWeight="bold"
          sx={{ color: COLORS.MainColor }}
        />
      </Box>
    </Box>
  );
};

export default RefundsPage;
